import { useState, useEffect, useCallback } from 'react';
import { categoryService, Category, CategoryAssignmentPayload } from '../services/categoryService';

// Hook for category hierarchy
export interface UseCategoryHierarchyReturn {
  categories: Category[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useCategoryHierarchy = (): UseCategoryHierarchyReturn => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async (signal?: AbortSignal) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await categoryService.getCategoryHierarchy(signal);
      setCategories(data);
    } catch (err) {
      if ((err as any)?.name === 'CanceledError' || (err as any)?.name === 'AbortError') {
        // Request was cancelled on unmount — swallow silently
        return;
      }
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch categories';
      setError(errorMessage);
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchCategories(controller.signal);
    return () => controller.abort();
  }, [fetchCategories]);

  return {
    categories,
    isLoading,
    error,
    refetch: () => fetchCategories(),
  };
};

// Hook for featured categories
export interface UseFeaturedCategoriesReturn {
  featuredCategories: Category[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useFeaturedCategories = (): UseFeaturedCategoriesReturn => {
  const [featuredCategories, setFeaturedCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFeaturedCategories = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await categoryService.getFeaturedCategories();
      setFeaturedCategories(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch featured categories';
      setError(errorMessage);
      setFeaturedCategories([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeaturedCategories();
  }, [fetchFeaturedCategories]);

  return {
    featuredCategories,
    isLoading,
    error,
    refetch: fetchFeaturedCategories,
  };
};

// Hook for category selection management
export interface CategorySelection {
  selectedCategories: number[];
  primaryCategoryId: number | null;
}

export interface UseCategorySelectionReturn {
  selection: CategorySelection;
  toggleCategory: (categoryId: number) => void;
  setPrimaryCategory: (categoryId: number) => void;
  clearSelection: () => void;
  isSelected: (categoryId: number) => boolean;
  isPrimary: (categoryId: number) => boolean;
  getSelectedCategories: (allCategories: Category[]) => Category[];
  getPrimaryCategory: (allCategories: Category[]) => Category | null;
  setSelection: (selection: CategorySelection) => void;
}

export const useCategorySelection = (
  initialSelection: CategorySelection = { selectedCategories: [], primaryCategoryId: null }
): UseCategorySelectionReturn => {
  const [selection, setSelection] = useState<CategorySelection>(initialSelection);

  const toggleCategory = useCallback((categoryId: number) => {
    console.log('toggleCategory called with:', categoryId);
    setSelection(prev => {
      console.log('toggleCategory setSelection, prev:', prev);
      const isCurrentlySelected = prev.selectedCategories.includes(categoryId);
      console.log('isCurrentlySelected:', isCurrentlySelected);
      
      if (isCurrentlySelected) {
        // Remove category
        const newSelectedCategories = prev.selectedCategories.filter(id => id !== categoryId);
        const newSelection = {
          selectedCategories: newSelectedCategories,
          primaryCategoryId: prev.primaryCategoryId === categoryId 
            ? (newSelectedCategories.length > 0 ? newSelectedCategories[0] : null)
            : prev.primaryCategoryId
        };
        console.log('toggleCategory removing, newSelection:', newSelection);
        return newSelection;
      } else {
        // Add category
        const newSelectedCategories = [...prev.selectedCategories, categoryId];
        const newSelection = {
          selectedCategories: newSelectedCategories,
          primaryCategoryId: prev.primaryCategoryId || categoryId // Set as primary if no primary exists
        };
        console.log('toggleCategory adding, newSelection:', newSelection);
        return newSelection;
      }
    });
  }, []);

  const setPrimaryCategory = useCallback((categoryId: number) => {
    setSelection(prev => {
      // Ensure the category is selected before setting as primary
      if (!prev.selectedCategories.includes(categoryId)) {
        return {
          selectedCategories: [...prev.selectedCategories, categoryId],
          primaryCategoryId: categoryId
        };
      }
      
      return {
        ...prev,
        primaryCategoryId: categoryId
      };
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelection({ selectedCategories: [], primaryCategoryId: null });
  }, []);

  const isSelected = useCallback((categoryId: number) => {
    const result = selection.selectedCategories.includes(categoryId);
    return result;
  }, [selection.selectedCategories]);

  const isPrimary = useCallback((categoryId: number) => {
    const result = selection.primaryCategoryId === categoryId;
    return result;
  }, [selection.primaryCategoryId]);

  const getSelectedCategories = useCallback((allCategories: Category[]) => {
    const flattenCategories = (categories: Category[]): Category[] => {
      return categories.reduce((acc: Category[], category) => {
        acc.push(category);
        if (category.children) {
          acc.push(...flattenCategories(category.children));
        }
        return acc;
      }, []);
    };

    const flatCategories = flattenCategories(allCategories);
    return flatCategories.filter(cat => selection.selectedCategories.includes(cat.categoryId));
  }, [selection.selectedCategories]);

  const getPrimaryCategory = useCallback((allCategories: Category[]) => {
    if (!selection.primaryCategoryId) return null;
    
    const flattenCategories = (categories: Category[]): Category[] => {
      return categories.reduce((acc: Category[], category) => {
        acc.push(category);
        if (category.children) {
          acc.push(...flattenCategories(category.children));
        }
        return acc;
      }, []);
    };

    const flatCategories = flattenCategories(allCategories);
    return flatCategories.find(cat => cat.categoryId === selection.primaryCategoryId) || null;
  }, [selection.primaryCategoryId]);

  const setSelectionWrapper = useCallback((newSelection: CategorySelection) => {
    console.log('setSelectionWrapper called with:', newSelection);
    console.log('Current selection before update:', selection);
    setSelection(newSelection);
    // Use setTimeout to log the updated state after React has processed the state update
    setTimeout(() => {
      console.log('setSelectionWrapper completed, checking state in next tick');
    }, 0);
  }, [selection]);

  return {
    selection,
    toggleCategory,
    setPrimaryCategory,
    clearSelection,
    isSelected,
    isPrimary,
    getSelectedCategories,
    getPrimaryCategory,
    setSelection: setSelectionWrapper,
  };
};

export default {
  useCategoryHierarchy,
  useFeaturedCategories,
  useCategorySelection,
};
